// The CRM import's wire (CPB-BE7): hand-modelled with the backend's exact property names, and a
// tolerant decode from `unknown` for every response, so a malformed body is a safe state rather
// than a crash. Counts are numbers the server computed; this module never derives one.

export type CrmImportField =
  | 'external_id'
  | 'first_name'
  | 'last_name'
  | 'full_name'
  | 'organization_name'
  | 'email'
  | 'email_2'
  | 'phone'
  | 'address_line_1'
  | 'address_line_2'
  | 'city'
  | 'state'
  | 'postal_code'
  | 'country'
  | 'tags'
  | 'notes'
  | 'last_gift_date'
  | 'first_gift_date'
  | 'last_gift_amount'
  | 'lifetime_giving'
  | 'largest_gift'
  | 'deceased'
  | 'deceased_date'
  | 'unsubscribed'
  | 'consent_source'
  | 'recurring_flag'
  | 'recurring_amount'
  | 'recurring_frequency';

export type CrmImportConsentBasis = 'gave' | 'signed_up' | 'took_part' | 'mixed';

export interface CrmImportColumnIn {
  column: number;
  field: CrmImportField;
  values?: string[];
}

export interface CrmImportSheetField {
  field: CrmImportField;
  header: string;
  description: string;
  many: boolean;
  takes_values: boolean;
}

export interface CrmImportSheet {
  fields: CrmImportSheetField[];
  csv_header: string;
  file_name: string;
  consent_bases: { value: CrmImportConsentBasis; description: string }[];
}

export interface CrmImportPreviewColumn {
  column: number;
  header: string;
  example: string | null;
  values: string[] | null;
  yes_values: Partial<Record<CrmImportField, string[]>>;
  suggested_field: CrmImportField | null;
  note: string | null;
}

export interface CrmImportPreview {
  file_name: string;
  file_format: string;
  file_sha256: string;
  row_count: number;
  columns: CrmImportPreviewColumn[];
  sample_rows: string[][];
  suggested_source_label: string;
}

export interface CrmImportIssue {
  row_number: number;
  field: string | null;
  message: string;
}

export interface CrmImportCheck {
  file_name: string;
  file_format: string;
  file_sha256: string;
  row_count: number;
  source_label: string | null;
  consent_basis: CrmImportConsentBasis | null;
  new_contacts: number;
  new_unsubscribed: number;
  new_without_email: number;
  new_without_email_rows: number[];
  combined: number;
  existing: number;
  existing_unsubscribed: number;
  left_out: number;
  /** Contacts already in the CRM that a deceased row matches, marked so they're never emailed. */
  existing_deceased: number;
  invalid: number;
  unsubscribed_addresses: number;
  issues: CrmImportIssue[];
  issues_truncated: boolean;
}

export interface CrmImportResult extends CrmImportCheck {
  batch_id: string;
  created_at: string | null;
}

const FIELDS: readonly CrmImportField[] = [
  'external_id',
  'first_name',
  'last_name',
  'full_name',
  'organization_name',
  'email',
  'email_2',
  'phone',
  'address_line_1',
  'address_line_2',
  'city',
  'state',
  'postal_code',
  'country',
  'tags',
  'notes',
  'last_gift_date',
  'first_gift_date',
  'last_gift_amount',
  'lifetime_giving',
  'largest_gift',
  'deceased',
  'deceased_date',
  'unsubscribed',
  'consent_source',
  'recurring_flag',
  'recurring_amount',
  'recurring_frequency',
];
const BASES: readonly CrmImportConsentBasis[] = ['gave', 'signed_up', 'took_part', 'mixed'];

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
}

function texts(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function field(value: unknown): CrmImportField | null {
  return typeof value === 'string' && (FIELDS as readonly string[]).includes(value)
    ? (value as CrmImportField)
    : null;
}

function basis(value: unknown): CrmImportConsentBasis | null {
  return typeof value === 'string' && (BASES as readonly string[]).includes(value)
    ? (value as CrmImportConsentBasis)
    : null;
}

export function decodeCrmImportSheet(value: unknown): CrmImportSheet | null {
  const body = record(value);
  if (body === null || !Array.isArray(body.fields)) return null;
  const fields: CrmImportSheetField[] = [];
  for (const raw of body.fields) {
    const row = record(raw);
    const key = field(row?.field);
    const header = text(row?.header);
    if (row === null || key === null || header === null) continue;
    fields.push({
      field: key,
      header,
      description: text(row.description) ?? '',
      many: row.many === true,
      takes_values: row.takes_values === true,
    });
  }
  const bases = (Array.isArray(body.consent_bases) ? body.consent_bases : [])
    .map((raw) => {
      const row = record(raw);
      const value = basis(row?.value);
      return value === null ? null : { value, description: text(row?.description) ?? '' };
    })
    .filter((row): row is { value: CrmImportConsentBasis; description: string } => row !== null);
  return {
    fields,
    csv_header: text(body.csv_header) ?? fields.map((row) => row.header).join(','),
    file_name: text(body.file_name) ?? 'givmo-contacts-import.csv',
    consent_bases: bases,
  };
}

export function decodeCrmImportPreview(value: unknown): CrmImportPreview | null {
  const body = record(value);
  if (body === null || !Array.isArray(body.columns)) return null;
  const columns: CrmImportPreviewColumn[] = [];
  for (const raw of body.columns) {
    const row = record(raw);
    if (row === null || typeof row.column !== 'number') continue;
    const yes: Partial<Record<CrmImportField, string[]>> = {};
    const rawYes = record(row.yes_values);
    if (rawYes !== null) {
      for (const [key, list] of Object.entries(rawYes)) {
        const known = field(key);
        if (known !== null) yes[known] = texts(list);
      }
    }
    columns.push({
      column: row.column,
      header: text(row.header) ?? `Column ${row.column + 1}`,
      example: text(row.example),
      values: Array.isArray(row.values) ? texts(row.values) : null,
      yes_values: yes,
      suggested_field: field(row.suggested_field),
      note: text(row.note),
    });
  }
  return {
    file_name: text(body.file_name) ?? '',
    file_format: text(body.file_format) ?? 'csv',
    file_sha256: text(body.file_sha256) ?? '',
    row_count: count(body.row_count),
    columns,
    sample_rows: (Array.isArray(body.sample_rows) ? body.sample_rows : []).map(texts),
    suggested_source_label: text(body.suggested_source_label) ?? '',
  };
}

function decodeIssue(value: unknown): CrmImportIssue | null {
  const row = record(value);
  const message = text(row?.message);
  if (row === null || message === null || typeof row.row_number !== 'number') return null;
  return { row_number: row.row_number, field: text(row.field), message };
}

export function decodeCrmImportCheck(value: unknown): CrmImportCheck | null {
  const body = record(value);
  if (body === null || typeof body.new_contacts !== 'number') return null;
  return {
    file_name: text(body.file_name) ?? '',
    file_format: text(body.file_format) ?? 'csv',
    file_sha256: text(body.file_sha256) ?? '',
    row_count: count(body.row_count),
    source_label: text(body.source_label),
    consent_basis: basis(body.consent_basis),
    new_contacts: count(body.new_contacts),
    new_unsubscribed: count(body.new_unsubscribed),
    new_without_email: count(body.new_without_email),
    new_without_email_rows: (Array.isArray(body.new_without_email_rows)
      ? body.new_without_email_rows
      : []
    ).filter((row): row is number => typeof row === 'number'),
    combined: count(body.combined),
    existing: count(body.existing),
    existing_unsubscribed: count(body.existing_unsubscribed),
    left_out: count(body.left_out),
    existing_deceased: count(body.existing_deceased),
    invalid: count(body.invalid),
    unsubscribed_addresses: count(body.unsubscribed_addresses),
    issues: (Array.isArray(body.issues) ? body.issues : [])
      .map(decodeIssue)
      .filter((row): row is CrmImportIssue => row !== null),
    issues_truncated: body.issues_truncated === true,
  };
}

export function decodeCrmImportResult(value: unknown): CrmImportResult | null {
  const check = decodeCrmImportCheck(value);
  const body = record(value);
  const batchId = text(body?.batch_id);
  if (check === null || batchId === null) return null;
  return { ...check, batch_id: batchId, created_at: text(body?.created_at) };
}
